import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Trash2, Globe, EyeOff, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-sand-200 text-ink placeholder-fog/50 text-sm focus:outline-none focus:border-ocean-700 transition-colors";

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("rn_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const headers = { "X-Admin-Password": password };

  const loadPosts = useCallback(() => {
    setLoadingPosts(true);
    axios
      .get(`${API}/admin/blog`, { headers })
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoadingPosts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  useEffect(() => {
    if (!password) return;
    axios
      .post(`${API}/admin/verify`, { password })
      .then(() => {
        setAuthed(true);
        sessionStorage.setItem("rn_admin", password);
        loadPosts();
      })
      .catch(() => {
        setAuthed(false);
        sessionStorage.removeItem("rn_admin");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify`, { password });
      setAuthed(true);
      sessionStorage.setItem("rn_admin", password);
      loadPosts();
    } catch {
      toast.error("Wrong password");
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/blog/generate`,
        { topic, keyword },
        { headers, timeout: 180000 }
      );
      toast.success(`Draft ready: "${res.data.title}" — review and publish below`);
      setTopic("");
      setKeyword("");
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed — try again");
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, { headers });
      toast.success(post.published ? "Unpublished" : "Published — live on /blog");
      loadPosts();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`, { headers });
      toast.success("Deleted");
      loadPosts();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!authed) {
    return (
      <div className="bg-sand-50 min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white border border-sand-200 rounded-2xl p-8 shadow-sm"
        >
          <h1 className="font-display text-2xl font-bold text-ocean-900">Raffiel Nias Admin</h1>
          <p className="text-xs text-fog mt-1 mb-6">Blog writer access</p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className={inputCls}
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-4 w-full py-3 rounded-xl bg-ocean-700 text-white text-sm font-semibold hover:bg-ocean-800 transition-colors"
          >
            Sign In
          </button>
          <Link to="/" className="block text-center text-xs text-fog mt-4 hover:text-ocean-700">
            &larr; Back to website
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-ocean-900" data-testid="admin-title">
              AI Blog Writer
            </h1>
            <p className="text-xs text-fog mt-1">
              Generate SEO stories about Nias surfing — review, publish, rank on Google.
            </p>
          </div>
          <Link to="/blog" className="text-xs font-semibold text-ocean-700 hover:underline">
            View blog &rarr;
          </Link>
        </div>

        <form
          onSubmit={generate}
          data-testid="blog-generate-form"
          className="bg-white border border-sand-200 rounded-2xl p-6 shadow-sm mb-10"
        >
          <label className="block text-xs font-mono-clean uppercase tracking-wider text-fog mb-2">
            Blog topic
          </label>
          <input
            data-testid="blog-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            minLength={3}
            placeholder="e.g. Best time to surf Lagundri Bay"
            className={inputCls}
          />
          <label className="block text-xs font-mono-clean uppercase tracking-wider text-fog mt-4 mb-2">
            Target SEO keyword (optional)
          </label>
          <input
            data-testid="blog-keyword-input"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. surf hotel nias"
            className={inputCls}
          />
          <button
            type="submit"
            data-testid="blog-generate-button"
            disabled={generating}
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-ocean-700 text-white text-sm font-semibold hover:bg-ocean-800 transition-colors disabled:opacity-60"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Writing your story (30-60s)..." : "Generate Draft with AI"}
          </button>
        </form>

        <h2 className="font-display text-xl font-bold text-ocean-900 mb-4">Your posts</h2>
        {loadingPosts ? (
          <p className="text-sm text-fog">Loading...</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-fog" data-testid="admin-empty-state">
            No posts yet — generate your first story above.
          </p>
        ) : (
          <div className="space-y-4" data-testid="admin-posts-list">
            {posts.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-post-${p.slug}`}
                className="bg-white border border-sand-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-mono-clean uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        p.published
                          ? "bg-ocean-700/10 text-ocean-700"
                          : "bg-terracotta/10 text-terracotta"
                      }`}
                    >
                      {p.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-[11px] font-mono-clean text-fog">
                      {new Date(p.created_at).toLocaleDateString("en-GB")}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-ocean-900 mt-1.5 truncate">{p.title}</h3>
                  <p className="text-xs text-fog truncate mt-0.5">{p.meta_description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.published && (
                    <Link
                      to={`/blog/${p.slug}`}
                      data-testid={`admin-view-${p.slug}`}
                      className="text-xs font-semibold text-ocean-700 hover:underline px-2"
                    >
                      View
                    </Link>
                  )}
                  <button
                    onClick={() => togglePublish(p)}
                    data-testid={`admin-publish-${p.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                      p.published
                        ? "bg-sand-100 text-fog hover:bg-sand-200"
                        : "bg-ocean-700 text-white hover:bg-ocean-800"
                    }`}
                  >
                    {p.published ? <EyeOff size={13} /> : <Globe size={13} />}
                    {p.published ? "Unpublish" : "Publish"}
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`admin-delete-${p.slug}`}
                    className="p-2 rounded-lg text-fog hover:text-terracotta hover:bg-terracotta/10 transition-colors"
                    aria-label="Delete post"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
